import { useState } from 'react';
import Icon from './Icon';
import type { BookTodaySummary } from '@/lib/all-reservations';

// Global reservation tracker CTA — ports mobile/components/you/ReservationTeaser.tsx.
// Five states, never a dead end, all linking to the cross-trip /reservations
// agenda. The summary is lib/all-reservations.ts summarizeBookToday, the
// same rows and counts the agenda shows:
//   1. windows open now      → "Book now across your trips"
//   2. a next window exists  → "Next to book: {name}", with the open moment
//                              in the venue's zone and yours
//   3. only undated entries  → "{n} need a date"
//   4. all tracked booked    → "All caught up"
//   5. nothing tracked       → "Track a reservation"
// `summary === null` = still loading (render nothing; the card fades in).

interface Content {
  badge: { kind: 'count' | 'days' | 'icon'; value?: number; icon?: string; tone: 'accent' | 'sage' };
  title: string;
  sub: string;
}

function contentFor(s: BookTodaySummary): Content {
  if (s.count > 0) {
    const names = s.tripNames.slice(0, 3).join(' · ');
    return {
      badge: { kind: 'count', value: s.count, tone: 'accent' },
      title: 'Book now across your trips',
      sub: names || 'open booking windows',
    };
  }
  if (s.next) {
    return {
      badge: { kind: 'days', value: s.next.days, tone: 'accent' },
      title: `Next to book: ${s.next.name}`,
      sub: `${s.next.tripName} · ${s.next.line}`,
    };
  }
  if (s.needsDate > 0) {
    return {
      badge: { kind: 'icon', icon: 'calendar', tone: 'accent' },
      title: `${s.needsDate} reservation${s.needsDate === 1 ? '' : 's'} need${s.needsDate === 1 ? 's' : ''} a date`,
      sub: 'set the table date to count down to its window',
    };
  }
  if (s.trackedTotal > 0) {
    return {
      badge: { kind: 'icon', icon: 'check', tone: 'sage' },
      title: 'All caught up',
      sub: 'every tracked reservation is booked',
    };
  }
  return {
    badge: { kind: 'icon', icon: 'plus', tone: 'accent' },
    title: 'Track a reservation',
    sub: 'watch a booking window across your trips',
  };
}

function Badge({ badge }: { badge: Content['badge'] }) {
  const bg = badge.tone === 'sage' ? 'var(--sage)' : 'var(--accent)';
  return (
    <span
      style={{
        width: 44, height: 44, flexShrink: 0, borderRadius: 12, background: bg, color: '#FCFAF6',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 2px 8px rgba(197,106,77,0.28)',
      }}
    >
      {badge.kind === 'icon' ? (
        <Icon name={badge.icon || 'bell'} size={20} />
      ) : (
        <span className="wa-display" style={{ fontSize: badge.kind === 'days' ? 17 : 20, lineHeight: 1 }}>
          {badge.kind === 'days' ? `${badge.value}d` : badge.value}
        </span>
      )}
    </span>
  );
}

export default function ReservationTeaser({
  summary,
  onOpen,
}: {
  summary: BookTodaySummary | null;
  onOpen: () => void;
}) {
  const [hover, setHover] = useState(false);
  if (!summary) return null;
  const c = contentFor(summary);
  return (
    <button
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 16, width: '100%', textAlign: 'left',
        marginTop: 18, padding: '16px 18px', borderRadius: 'var(--r-lg)', cursor: 'pointer',
        background: 'var(--surface)', border: '1px solid var(--line)', fontFamily: 'var(--font-ui)',
        boxShadow: hover ? 'var(--shadow-lifted)' : 'var(--shadow-card)',
        transform: hover ? 'translateY(-2px)' : 'none',
        transition: 'box-shadow var(--dur), transform var(--dur)',
        animation: 'wa-fade-up var(--dur-slow) var(--ease)',
      }}
    >
      <Badge badge={c.badge} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="wa-eyebrow" style={{ marginBottom: 4 }}>Reservation tracker</div>
        <div className="wa-display" style={{ fontSize: 20, lineHeight: 1.05, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {c.title}
        </div>
        <div className="wa-mono" style={{ fontSize: 11, color: 'var(--ink-mute)', marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {c.sub}
        </div>
      </div>
      <span style={{ flexShrink: 0, color: 'var(--ink-mute)', display: 'flex', transform: hover ? 'translateX(3px)' : 'none', transition: 'transform var(--dur)' }}>
        <Icon name="chevR" size={18} />
      </span>
    </button>
  );
}
